import chunk from 'lodash/chunk';
import { useMemo, useState } from 'react';
import Marquee from 'react-fast-marquee';
import { Slide } from 'react-slideshow-image';
import { twc } from 'react-twc';

import { GalleryLeftArrowImg, GalleryRightArrowImg } from '@/assets';
import { ICON_PAGE_SIZE } from '@/constants';
import AssetCollapseContext from '@/context/AssetCollapseContext';
import GalleryCardsContext from '@/context/GalleryCardsContext';
import useScreenSize from '@/hooks/useScreenSize';
import { cn } from '@/utils/styles';

import GalleryCard from './GalleryCard';

const { useGalleryCardsTracked } = GalleryCardsContext;
const { useAssetCollapseTracked } = AssetCollapseContext;

interface GalleryCardsProps {
  className?: string;
}

function GalleryCards({ className }: GalleryCardsProps) {
  const [galleryCards] = useGalleryCardsTracked();
  const size = useScreenSize();
  const chunkValue = useMemo(() => {
    switch (size) {
      case '2xl':
        return 4;
      case 'xl':
      case 'lg':
      case 'md':
        return 3;
      case 'sm':
        return 2;
      case 'xs':
        return 1;
    }
  }, [size]);

  const chunkCards = useMemo(() => chunk(galleryCards, chunkValue), [chunkValue, galleryCards]);
  const [shownChunkIdx, setShownChunkIdx] = useState(0);
  const disabledPrev = shownChunkIdx === 0;
  const disabledNext = shownChunkIdx === chunkCards.length - 1;

  return (
    <OuterCardsAbsoluteContainer className={cn(className)}>
      <Slide
        transitionDuration={500}
        infinite={false}
        indicators={false}
        arrows={true}
        autoplay={false}
        prevArrow={
          <PrevButton
            className={cn('hidden sm:flex', disabledPrev ? '!cursor-default' : '!cursor-pointer')}
            disabled={disabledPrev}
          >
            <img src={GalleryLeftArrowImg} />
          </PrevButton>
        }
        nextArrow={
          <NextButton
            className={cn('hidden sm:flex', disabledNext ? '!cursor-default' : '!cursor-pointer')}
            disabled={disabledNext}
          >
            <img src={GalleryRightArrowImg} />
          </NextButton>
        }
        onChange={(_, to) => {
          setShownChunkIdx(to);
        }}
      >
        {chunkCards.map((row, idx) => {
          return (
            <GalleryCardsStyle key={idx}>
              {row.map((props, idx) => (
                <GalleryCard key={idx} {...props} />
              ))}
            </GalleryCardsStyle>
          );
        })}
      </Slide>
    </OuterCardsAbsoluteContainer>
  );
}

export function CoinList({ className }: { className?: string }) {
  const [galleryCards] = useGalleryCardsTracked();
  const [, setAssetCollapse] = useAssetCollapseTracked();

  const coinIcons = useMemo(() => {
    return galleryCards.map((card) => card.icon);
  }, [galleryCards]);

  const play = coinIcons.length > ICON_PAGE_SIZE;

  const coinIconsContent = coinIcons.map((coinIcon, idx) => (
    <img className={'w-10 h-10 mr-4 sm:mr-5 md:mr-6'} key={idx} src={coinIcon} />
  ));

  return (
    <OuterCoinsAbsoluteContainer className={cn(className)}>
      <CoinListStyle onClick={() => setAssetCollapse(false)}>
        {play ? (
          <Marquee play={play}>{coinIconsContent}</Marquee>
        ) : (
          <CenterLayout>{coinIconsContent}</CenterLayout>
        )}
      </CoinListStyle>
    </OuterCoinsAbsoluteContainer>
  );
}

const CenterLayout = twc.div`
  w-full flex justify-center
`;

const commonInnerStyle = `
  max-w-[1024px] mx-8 md:mx-auto
  flex items-center
`;

const commonOuterStyle = `
  absolute w-full h-auto
`;

// 576 = (40 + 24) * 9 = (icon width + gap) * max number
const CoinListStyle = twc.div`
  ${commonInnerStyle} justify-start
  max-w-[576px] overflow-x-scroll cursor-pointer
`;

const GalleryCardsStyle = twc.div`
  ${commonInnerStyle} justify-center gap-3 md:gap-10
`;

const OuterCardsAbsoluteContainer = twc.div`
  ${commonOuterStyle} top-[310px] md:top-[380px] translate-y-[-50%] duration-300
`;

const OuterCoinsAbsoluteContainer = twc.div`
  ${commonOuterStyle} top-[240px] duration-300
`;

const commonControllerStyle = `
  absolute top-[50%] translate-y-[-50%]
`;
const PrevButton = twc.button`
  ${commonControllerStyle}
  ml-[1%] md:ml-[5%] disabled:opacity-0
`;

const NextButton = twc.button`
  ${commonControllerStyle}
  mr-[1%] md:mr-[5%] duration-300
  disabled:opacity-0
`;
export default GalleryCards;
